import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';

import { LifecycleStatus } from '@ndvr/portfolio-api';

import { ConfirmModal } from '@/components/ConfirmModal';
import { EditableCardGroupProvider } from '@/components/EditableCardGroupContext';
import { useAssetsByIdsMap } from '@/hooks/market-data-queries';
import { useRequiredParams } from 'utils';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import {
    getConvictions,
    getLifestyleProtectionInfo,
} from 'views/portfolio/PortfolioPage/tabs/target-allocation/allocationUtils';
import { ConvictionsCard } from 'views/portfolio/PortfolioPage/tabs/target-allocation/ConvictionsCard';
import { LifestyleProtectionCard } from 'views/portfolio/PortfolioPage/tabs/target-allocation/LifestyleProtectionCard';

import { GrowthAllocationCard } from './GrowthAllocationCard';
import { TargetAllocationChart } from './TargetAllocationChart';

export const TargetAllocationTab = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);

    const { data: accountGroupProfile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    const convictionSecurityIds = useMemo(() => {
        const ids = new Set<string>();
        accountGroupProfile?.accounts?.forEach((account) => {
            account.convictions?.forEach((conviction) => {
                ids.add(conviction.securityId);
            });
        });
        return Array.from(ids);
    }, [accountGroupProfile]);

    const { data: assetsMap } = useAssetsByIdsMap(convictionSecurityIds);

    const lifestyleProtectionInfo = getLifestyleProtectionInfo(accountGroupProfile);
    const convictions = getConvictions(accountGroupProfile, assetsMap);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasLifestyleChanges, setHasLifestyleChanges] = useState(false);
    const [hasConvictionChanges, setHasConvictionChanges] = useState(false);
    const [hasGrowthChanges, setHasGrowthChanges] = useState(false);
    const [selectedAccountGUID, setSelectedAccountGUID] = useState<string | undefined>();

    const handleAccountSelect = (accountGUID: string | undefined) => {
        setSelectedAccountGUID(accountGUID);
    };

    useEffect(() => {
        setHasUnsavedChanges(hasLifestyleChanges || hasConvictionChanges || hasGrowthChanges);
    }, [hasLifestyleChanges, hasConvictionChanges, hasGrowthChanges]);

    const blocker = useBlocker(useCallback(() => hasUnsavedChanges, [hasUnsavedChanges]));

    return (
        <EditableCardGroupProvider>
            <div className="grid grid-cols-4 items-start gap-6">
                <GrowthAllocationCard
                    accountGroupProfile={accountGroupProfile}
                    onUpdate={setHasGrowthChanges}
                    onAccountSelect={handleAccountSelect}
                />

                <div className="flex flex-col gap-6">
                    <LifestyleProtectionCard
                        accounts={accountGroupProfile?.accounts ?? []}
                        lifestyleProtection={lifestyleProtectionInfo}
                        onUpdate={setHasLifestyleChanges}
                    />
                    <ConvictionsCard
                        accounts={accountGroupProfile?.accounts ?? []}
                        convictions={convictions || {}}
                        onUpdate={setHasConvictionChanges}
                    />
                </div>
                <div>
                    {accountGroupProfile?.accountGroup.lifecycleStatus === LifecycleStatus.REAL && (
                        <TargetAllocationChart
                            accountGroupGUID={accountGroupGUID}
                            accounts={accountGroupProfile?.accounts ?? []}
                            selectedAccountGUID={selectedAccountGUID}
                        />
                    )}
                </div>
            </div>

            <ConfirmModal
                open={blocker.state === 'blocked'}
                onOpenChange={(open) => {
                    if (!open) blocker.reset?.();
                }}
                title="You have unsaved changes"
                description="Are you sure you want to leave? Your unsaved changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                confirmVariant="destructive"
                onConfirm={() => blocker.proceed?.()}
                onCancel={() => blocker.reset?.()}
            />
        </EditableCardGroupProvider>
    );
};
